import React, { useState } from "react";
import {
    Document,
    Page,
    Image,
    StyleSheet,
    pdf
} from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import Head from 'next/head';
import { FaFileUpload } from 'react-icons/fa';
import { AiOutlineClose } from 'react-icons/ai';
import { FaDownload } from 'react-icons/fa';
import styles from "../../signature.module.css";
import Blog from "../../../../components/CalculatorBlogs/Blog";
import SignaturePopup from "../../../../components/SignaturePopup";
import PdfToolsLinks from "../../../../components/PdfToolsLinks";

const PAGE_SIZES = ['4A0', '2A0', 'A0', 'A1', 'A2', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'A9', 'A10', 'B0', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10', 'C0', 'C1', 'C2', 'C3', 'C4', 'C5', 'C6', 'C7', 'C8', 'C9', 'C10', 'RA0', 'RA1', 'RA2', 'RA3', 'RA4', 'SRA0', 'SRA1', 'SRA2', 'SRA3', 'SRA4', 'EXECUTIVE', 'FOLIO', 'LEGAL', 'LETTER', 'TABLOID', 'ID1']

const BlogData = [
    {
        id: 1,
        question: "What is HEIC to PDF Tool?",
        answer: (
            <div className="">
                <p className="">
                    A HEIC to PDF tool is a utility that allows you to convert HEIC and HEIF image files into PDF documents. This tool enables you to combine multiple HEIC and HEIF images into a single PDF file, making it convenient for various purposes such as creating presentations or sharing multiple images as a document.
                </p>
            </div>
        ),
    },
    {
        id: 2,
        question: "How to Convert HEIC and HEIF Images to PDFs Online?",
        answer: (
            <div className="">
                <p className="">
                    To convert HEIC and HEIF images to PDFs online, you can upload the HEIC/HEIF images you want to convert. The tool will process the HEIC/HEIF files and arrange them into a PDF document. After the conversion, you can download the PDF file containing all the HEIC/HEIF images.
                </p>
            </div>
        ),
    },
    {
        id: 3,
        question: "How to Use the HEIC to PDF Conversion Tool?",
        answer: (
            <div className="">
                <p className="">
                    Using the HEIC to PDF conversion tool is simple. Upload your HEIC and HEIF files, and the tool will automatically arrange them into a PDF document. You can customize the order of the images if needed. Once the arrangement is satisfactory, you can download the PDF file.
                </p>
            </div>
        ),
    },
    {
        id: 4,
        question: "Are There Any Limitations on HEIC/HEIF Size or Number of Images?",
        answer: (
            <div className="">
                <p className="">
                    HEIC to PDF tools typically support a wide range of HEIC and HEIF sizes and quantities. However, very large HEIC/HEIF files might take longer to process. While there are usually no strict limitations on the number of images, {`it's`} advisable to review the specific {`tool's`} guidelines for any restrictions.
                </p>
            </div>
        ),
    },
    {
        id: 5,
        question: "Is the HEIC to PDF Conversion Process Secure?",
        answer: (
            <div className="">
                <p className="">
                    Yes, reputable online HEIC to PDF conversion tools prioritize user privacy and security. They utilize encryption protocols to ensure the safety of your files during the conversion process. However, {`it's`} important to use trusted and well-known platforms to convert your sensitive HEIC and HEIF images into PDF documents.
                </p>
            </div>
        ),
    },
];


const DocumentPdf = ({ images = [], pageSize, orientation, imageAlignment }) => {
    const pdfStyles = StyleSheet.create({
        viewer: {
            width: "1000px",
            height: "1000px",
        },
        page: {
            backgroundColor: "#ffffff",
            color: "#000000",
            display: "flex",
            justifyContent: imageAlignment,
            alignItems: "center",
        },
        image: {
            width: "100%",
            height: "auto",
        },
    });

    return (
        <Document>
            {images.map((image, index) => (
                <Page key={index} size={pageSize} style={pdfStyles.page} orientation={orientation} wrap>
                    <Image src={image} style={pdfStyles.image} alt={``} />
                </Page>
            ))}
        </Document>
    );
};

const HeicToPdf = () => {
    const [images, setImages] = useState([]);
    const [loader, setLoader] = useState(false);
    const [pageRendering, setPageRendering] = useState(false)
    const [pageSize, setPageSize] = useState('A4')
    const [orientation, setOrientation] = useState('portrait')
    const [imageAlignment, setImageAlignment] = useState('center')
    const [signaturePopup, setSignaturePopup] = useState(false);

    const scrollToPreview = () => {
        const element = document.getElementById('preview-pdfs');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const handleDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        handleImageUpload(files);
        scrollToPreview();
    };

    const handleUpload = (e) => {
        const files = e.target.files;
        handleImageUpload(files)
        if (files) {
            scrollToPreview();
        }
    };

    const handleImageUpload = async (files) => {
        setPageRendering(true)
        const imageArray = [];
        for (let i = 0; i < files.length; i++) {
            if (typeof window !== 'undefined') {
                const heic2any = require('heic2any');
                const blob = await heic2any({
                    blob: files[i],
                    toType: 'image/jpeg',
                    quality: 0.8
                })
                const imageUrl = URL.createObjectURL(blob);
                imageArray.push(imageUrl);
            }
            if (imageArray.length === files.length) {
                setImages([...images, ...imageArray])
            }
        }
        setPageRendering(false)
    };

    const handleDownload = async () => {
        setLoader(true)
        const doc = <DocumentPdf images={images} pageSize={pageSize} orientation={orientation} imageAlignment={imageAlignment} />;
        const asPdf = pdf([]);
        asPdf.updateContainer(doc);
        const blob = await asPdf.toBlob();
        saveAs(blob, "document.pdf");
        setLoader(false)
        setSignaturePopup(true)
    }

    const removeImage = (index) => {
        const newImages = [...images];
        newImages.splice(index, 1);
        setImages(newImages);
    }

    return (
        <>
            <div className="w-full mt-20 sm:mt-24">
                <Head>
                    <meta charSet="utf-8" />
                    <title>HEIC/HEIF to PDF Tool | BoloSign</title>
                    <meta name="description" content="Convert HEIC and HEIF images to PDF documents online for free. No software installation required." />
                    <meta name="keywords" content="heic to pdf, heic to pdf converter, convert heic to pdf, heic to pdf online, heic to pdf free, heic to pdf tool, heic to pdf conversion, heic to pdf converter online, heic to pdf converter free, heic to pdf converter tool, image to pdf, image to pdf converter, convert image to pdf, heif to pdf, heif to pdf converter, convert heif to pdf, heif to pdf online, heif to pdf free, heif to pdf tool, heif to pdf conversion, heif to pdf converter online, heif to pdf converter free, heif to pdf converter tool, image to pdf, image to pdf converter, convert image to pdf, heic format to pdf, heif file to pdf, heif image to pdf" />
                    <meta name="author" content="BoloSign" />
                    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                    <link rel="canonical" href="https://www.bolosign.com/signature/pdf-tools/heic-to-pdf" />
                    <meta property="og:title" content="HEIC to PDF Tool | BoloSign" />
                    <meta property="og:description" content="Convert HEIC and HEIF images to PDF documents online for free. No software installation required." />
                    <meta property="og:url" content="https://www.bolosign.com/signature/pdf-tools/heic-to-pdf" />
                    <meta property="og:site_name" content="BoloSign" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="630" />
                    <meta property="og:image:alt" content="BoloSign" />
                    <meta property="og:type" content="website" />
                    <meta property="og:locale" content="en_US" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:site" content="@BoloForms" />
                    <meta name="twitter:creator" content="@BoloForms" />
                    <meta name="twitter:title" content="HEIC to PDF Tool | BoloSign" />
                    <meta name="twitter:description" content="Convert HEIC and HEIF images to PDF documents online for free. No software installation required." />
                </Head>

                <div className="">
                    <div className="max-w-[1280px] mx-auto px-2 md:px-10 py-20 rounded-xl relative flex flex-col gap-20">
                        <div className="text-violet-500 text-center">
                            <h1 className="text-3xl md:text-6xl text-gray-800">
                                HEIC/HEIF To PDF Tool
                            </h1>
                            <p className="text-lg md:text-2xl mt-4 text-gray-600">
                                Online HEIC/HEIF To PDF Converter Tool For Free
                            </p>
                        </div>
                        <div
                            className="extraOutline p-4 bg-white bg-whtie mx-auto rounded-lg w-full md:w-[600px]"
                            onDragEnter={(e) => e.preventDefault()}
                            onDragOver={(e) => e.preventDefault()}
                            onDragLeave={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                        >
                            <div className="file_upload px-2 md:px-20 py-5 md:py-14 relative border-4 border-dotted border-gray-300 rounded-lg">

                                <FaFileUpload className="text-gray-400 w-20 md:w-32 mx-auto mb-6 md:mb-10 text-[80px] md:text-[120px]" />
                                <div className="input_field flex flex-col w-full mx-auto text-center">
                                    <label>
                                        <input
                                            onChange={handleUpload}
                                            className="text-sm cursor-pointer w-36 hidden"
                                            type="file"
                                            accept=".heic,.heif,.heics,.heifs"
                                            multiple
                                        />
                                        <div className="text bg-violet-600 text-white text-xl border border-gray-300 rounded-xl font-semibold cursor-pointer py-3 md:py-4 hover:bg-indigo-500">
                                            Upload HEIC/HEIF Images
                                        </div>
                                    </label>

                                    <div className="title text-gray-500 uppercase text-lg md:text-xl">
                                        or drop files here
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id="preview-pdfs" className="max-w-[1280px] mx-auto flex flex-col gap-14 justify-center items-center">
                        {images.length > 0 && (
                            <>
                                <h1 className="text-3xl font-light text-center text-gray-700">
                                    Remove the image which you {`don't`} want
                                </h1>
                                <div className="">
                                    <button onClick={() => setImages([])} className="bg-violet-600 mx-auto w-full sm:w-[190px] py-2 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400">Remove All</button>
                                </div>
                                <div
                                    className="relative w-full h-[400px] flex flex-wrap justify-center gap-4 border-2 md:px-10 py-4 md:py-14 md:rounded-xl overflow-y-scroll scrollbar-thin scrollbar-thumb-violet-500 scrollbar-rounded-[12px]"
                                >
                                    {
                                        images.map((image, index) =>
                                        (
                                            <div
                                                key={index}
                                                className="border bg-white drop-shadow-2xl w-fit relative flex justify-center items-center flex-wrap"
                                            >
                                                <div className="p-4 bg-white w-[250px]">
                                                    <img id="image-generated" src={image} alt="pdfImage" className="w-full" />
                                                </div>
                                                <div className="absolute top-2 left-2 w-8 h-8 bg-violet-500 rounded-full text-xl text-white flex justify-center items-center cursor-pointer">
                                                    {index + 1}
                                                </div>
                                                <div
                                                    onClick={() => removeImage(index)}
                                                    className="absolute top-2 right-2 w-8 h-8 bg-gray-400 rounded-full flex justify-center items-center cursor-pointer"
                                                >
                                                    <AiOutlineClose className="text-white text-xl" />
                                                </div>
                                            </div>

                                        ))
                                    }
                                    <div id="page-loader" className={`${pageRendering ? "absolute w-full top-0 left-0 h-full flex justify-center items-center bg-white z-50" : "hidden"}`}>
                                        <svg aria-hidden="true" className="w-14 h-14 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-violet-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                                        </svg>
                                    </div>
                                </div>
                            </>
                        )}
                        {images.length > 0 && (
                            <div id="split-pdfs" className="w-full">
                                <div className="mb-10 flex flex-col justify-center items-center gap-4">

                                    <div className="flex justify-center items-center gap-4 flex-wrap">
                                        <div className="">
                                            <span className="text-gray-600 text-lg">Page Size : </span>
                                            <select
                                                onChange={(e) => setPageSize(e.target.value)}
                                                className="bg-white text-violet-600 outline-none w-fit p-2 rounded-lg shadow-md border border-violet-500"
                                                value={pageSize}
                                                name="" id="">
                                                {
                                                    PAGE_SIZES.map((size, index) => (
                                                        <option key={index} value={size}>{size}</option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                        <div className="">
                                            <span className="text-gray-600 text-lg">Orientation : </span>
                                            <select
                                                onChange={(e) => setOrientation(e.target.value)}
                                                className="bg-white text-violet-600 outline-none w-fit p-2 rounded-lg shadow-md border border-violet-500"
                                                value={orientation}
                                                name="" id="">
                                                <option value="portrait">Portrait</option>
                                                <option value="landscape">Landscape</option>
                                            </select>
                                        </div>
                                        <div className="">
                                            <span className="text-gray-600 text-lg">Image Align : </span>
                                            <select
                                                onChange={(e) => setImageAlignment(e.target.value)}
                                                className="bg-white text-violet-600 outline-none w-fit p-2 rounded-lg shadow-md border border-violet-500"
                                                value={imageAlignment}
                                                name="" id="">
                                                <option value="flex-start">Top</option>
                                                <option value="center">Center</option>
                                                <option value="flex-end">Bottom</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={handleDownload}
                                    className="bg-violet-600 border-none outline-none mx-auto w-full sm:w-[350px] py-4 text-xl rounded-lg font-semibold text-white flex items-center justify-center gap-4 hover:bg-violet-400"
                                >
                                    {loader ? (
                                        <div
                                            className={`flex items-center text-white space-x-2 ${styles.loader}`}
                                        >
                                            <div className="">Downloading</div>
                                            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                                        </div>
                                    ) : (
                                        <>
                                            Download Pdf <FaDownload className="text-3xl" />
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                    {
                        signaturePopup && <SignaturePopup onPopupClose={() => setSignaturePopup(false)} source="HEIC_TO_PDF" />
                    }

                    <div className="mt-20">
                        <div className="mb-20">
                            <PdfToolsLinks />
                        </div>
                        <Blog heading="HEIC To PDF Tool FAQs" BlogData={BlogData} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default HeicToPdf;
